import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  MapPin,
  Ruler,
  Euro,
  ChevronRight,
  FileText,
  Image,
  Download,
  RefreshCw,
  ChevronLeft,
  Send,
  Phone,
  Mail,
  User,
  MessageSquare,
  LogOut,
  Search,
  X,
  Sparkles,
  ClipboardCheck,
} from "lucide-react";
import { Card, CardContent } from "./ui/card";
import PageHeader from "./PageHeader";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/images/logo-immobiliare.png";
const LOGO_MARK_URL = "/images/logo-immobiliare-mark.png";

export const getPhotoUrl = (photo, size = "preview") => {
  if (!photo) return null;
  const preferred = size === "large" ? photo.large_url : photo.preview_url;
  const url = preferred || photo.url;
  if (!url) return null;
  if (url.startsWith("/")) return `${API_URL}${url}`;
  return url;
};

export const formatPrezzoVendita = (prezzo) => {
  if (!prezzo || prezzo === 0) return "Affitto e Cessione licenza";
  if (typeof prezzo === "number") return `€${prezzo.toLocaleString("it-IT")}`;
  return prezzo;
};

export const formatMq = (sqm) => {
  if (!sqm || sqm === 0) return "Attesa documenti";
  return `${sqm} mq`;
};

export default function ImmobiliareContent({
  token,
  userInfo = {},
  listEndpoint = "/api/immobili",
  richiestaEndpoint = "/api/immobili/richiesta",
  showHeader = true,
  portalMode = false,
  onLogout,
  contentEnabled = true,
}) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    motivo: "informazioni",
    nome: "",
    telefono: "",
    email: "",
    richieste: "",
  });
  const [sendingRequest, setSendingRequest] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredProperties = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter((property) => {
      const haystack = [
        property.name,
        property.address,
        property.location,
        property.provincia,
        property.note,
        property.tipo,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [properties, searchQuery]);

  const nuoveCount = properties.filter((p) => p.status === "Nuova").length;
  const inValutazioneCount = properties.filter((p) =>
    ["In valutazione", "SOPRALLUOGO"].includes(p.status)
  ).length;

  useEffect(() => {
    if (userInfo?.name || userInfo?.email) {
      setContactForm((prev) => ({
        ...prev,
        nome: userInfo.name || prev.nome,
        email: userInfo.email || prev.email,
        telefono: userInfo.telefono || prev.telefono,
      }));
    }
  }, [userInfo]);

  const fetchProperties = useCallback(async () => {
    if (!token || !contentEnabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}${listEndpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = response.data.data || [];
      setProperties(items);
      localStorage.setItem("affidya_immobiliare_seen_count", items.length.toString());
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Errore nel caricamento degli immobili");
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [token, listEndpoint, contentEnabled]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleSendRequest = async () => {
    if (!contactForm.nome || !contactForm.email) {
      toast.error("Inserisci nome e email");
      return;
    }
    setSendingRequest(true);
    try {
      await axios.post(
        `${API_URL}${richiestaEndpoint}`,
        {
          property_id: selectedProperty?.id,
          property_name: selectedProperty?.name || selectedProperty?.address,
          ...contactForm,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Richiesta inviata con successo!");
      setShowContactModal(false);
      setContactForm((prev) => ({ ...prev, richieste: "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSendingRequest(false);
    }
  };

  const getStatusBadge = (status, compact = false) => {
    const statusMap = {
      Nuova: {
        style: compact
          ? "bg-blue-600 text-white shadow-md ring-0 backdrop-blur-sm"
          : "bg-blue-500/10 text-blue-700 ring-blue-500/20",
        label: "Nuova",
      },
      "In valutazione": {
        style: compact
          ? "bg-amber-600 text-white shadow-md ring-0 backdrop-blur-sm"
          : "bg-amber-500/10 text-amber-800 ring-amber-500/20",
        label: "In valutazione",
      },
      SOPRALLUOGO: {
        style: compact
          ? "bg-amber-600 text-white shadow-md ring-0 backdrop-blur-sm"
          : "bg-amber-500/10 text-amber-800 ring-amber-500/20",
        label: "Sopralluogo",
      },
      TRATTATIVA: {
        style: compact
          ? "bg-emerald-600 text-white shadow-md ring-0 backdrop-blur-sm"
          : "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20",
        label: "Trattativa",
      },
      CHIUSO: {
        style: compact
          ? "bg-slate-700 text-white shadow-md ring-0 backdrop-blur-sm"
          : "bg-slate-500/10 text-slate-600 ring-slate-500/15",
        label: "Chiuso",
      },
    };
    const config = statusMap[status] || {
      style: compact
        ? "bg-slate-700 text-white shadow-md ring-0 backdrop-blur-sm"
        : "bg-slate-500/10 text-slate-600 ring-slate-500/15",
      label: status || "N/A",
    };
    return (
      <Badge
        className={`${config.style} ${compact ? "ring-0 border-0" : "ring-1 border-0"} font-semibold rounded-full ${compact ? "text-[11px] px-2.5 py-1" : "text-xs px-2.5 py-1"}`}
      >
        {config.label}
      </Badge>
    );
  };

  const downloadPlanimetria = () => {
    if (!selectedProperty?.planimetria_url) return;
    const url = selectedProperty.planimetria_url.startsWith("/")
      ? `${API_URL}${selectedProperty.planimetria_url}`
      : selectedProperty.planimetria_url;
    window.open(url, "_blank");
  };

  const handleCloseDetails = () => {
    setSelectedProperty(null);
    setSelectedPhotoIndex(0);
  };

  const handlePrevPhoto = () => {
    if (selectedProperty?.photos?.length > 0) {
      setSelectedPhotoIndex((prev) =>
        prev === 0 ? selectedProperty.photos.length - 1 : prev - 1
      );
    }
  };

  const handleNextPhoto = () => {
    if (selectedProperty?.photos?.length > 0) {
      setSelectedPhotoIndex((prev) =>
        prev === selectedProperty.photos.length - 1 ? 0 : prev + 1
      );
    }
  };

  const openProperty = (property) => {
    setSelectedProperty(property);
    setSelectedPhotoIndex(0);
  };

  if (loading) {
    return (
      <div className={`${portalMode ? "min-h-[100dvh] bg-[#F4F6FA]" : ""} p-4 md:p-6 lg:p-8 max-w-6xl mx-auto`}>
        <div className="space-y-4 animate-pulse">
          <div className="h-14 rounded-2xl bg-white/80" />
          <div className="flex gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-[88px] min-w-[160px] flex-1 rounded-2xl bg-white/80" />
            ))}
          </div>
          {[1, 2].map((i) => (
            <div key={i} className="rounded-3xl bg-white/80 overflow-hidden">
              <div className="aspect-[16/10] bg-slate-200/60" />
              <div className="p-5 space-y-3">
                <div className="h-5 w-2/3 rounded-lg bg-slate-200/60" />
                <div className="h-4 w-1/2 rounded-lg bg-slate-200/60" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const listContent = (
    <>
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Cerca città, provincia, tipologia..."
          className="h-[52px] pl-12 pr-11 rounded-2xl bg-white border-slate-200/80 text-base shadow-sm shadow-slate-200/40 placeholder:text-slate-400"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 active:scale-95 transition-all"
            aria-label="Cancella ricerca"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
        {[
          {
            value: properties.length,
            label: "Totale",
            icon: Building2,
            iconClass: "text-[#4068E8]",
            accentClass: "from-[#4068E8]/10 to-transparent",
          },
          {
            value: nuoveCount,
            label: "Nuove",
            icon: Sparkles,
            iconClass: "text-blue-500",
            accentClass: "from-blue-500/10 to-transparent",
          },
          {
            value: inValutazioneCount,
            label: "In valutazione",
            icon: ClipboardCheck,
            iconClass: "text-amber-500",
            accentClass: "from-amber-500/10 to-transparent",
          },
        ].map((stat) => (
          <Card
            key={stat.label}
            className="min-w-0 rounded-2xl border-0 bg-white shadow-sm shadow-slate-200/60 ring-1 ring-slate-200/60 overflow-hidden"
          >
            <CardContent className="relative p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3.5">
              <div
                className={`pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l ${stat.accentClass}`}
                aria-hidden
              />
              <div className="relative flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center shrink-0">
                <stat.icon className={`w-6 h-6 sm:w-7 sm:h-7 ${stat.iconClass}`} strokeWidth={2.25} />
              </div>
              <div className="relative min-w-0 flex-1">
                <p className="text-[10px] sm:text-[11px] font-semibold text-slate-500 uppercase tracking-wide sm:tracking-wider leading-tight">
                  {stat.label}
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 tabular-nums tracking-tight leading-none mt-1">
                  {stat.value}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Opportunità</h2>
          <p className="text-sm text-slate-500 mt-1">
            {filteredProperties.length} {filteredProperties.length === 1 ? "immobile" : "immobili"} disponibili
          </p>
        </div>
        <Sparkles className="w-5 h-5 text-blue-400 shrink-0 hidden sm:block" aria-hidden />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {filteredProperties.map((property) => {
          const firstPhotoUrl = property.photos?.length > 0 ? getPhotoUrl(property.photos[0], "preview") : null;
          const title = property.name || property.address || property.location;
          const locationLine = [property.location, property.provincia].filter(Boolean).join(" · ");

          return (
            <Card
              key={property.id}
              className="group overflow-hidden cursor-pointer rounded-3xl border-0 bg-white shadow-sm shadow-slate-200/60 ring-1 ring-slate-200/60 hover:shadow-lg hover:ring-blue-200/60 transition-all duration-200 active:scale-[0.99]"
              onClick={() => openProperty(property)}
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-[1.03]"
                  style={{
                    backgroundImage: firstPhotoUrl
                      ? `url(${firstPhotoUrl})`
                      : "linear-gradient(135deg, #fb923c 0%, #ea580c 100%)",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-black/5" />
                <div className="absolute inset-0 p-4 flex flex-col justify-between">
                  <div className="flex items-start justify-between gap-2">
                    {getStatusBadge(property.status, true)}
                    {property.photos?.length > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-black/45 backdrop-blur-sm px-2.5 py-1 text-xs font-medium text-white">
                        <Image className="w-3.5 h-3.5" />
                        {property.photos.length}
                      </span>
                    )}
                  </div>
                  {!firstPhotoUrl && (
                    <Building2 className="absolute bottom-4 right-4 w-14 h-14 text-white/25" />
                  )}
                  <div className="text-white">
                    <p className="font-bold text-lg leading-snug line-clamp-2 drop-shadow-sm">{title}</p>
                    {locationLine && (
                      <p className="text-sm text-white/90 mt-1 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{locationLine}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <CardContent className="p-4 sm:p-5 pt-4">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700">
                    <Ruler className="w-3.5 h-3.5 text-slate-400" />
                    {formatMq(property.sqm)}
                  </span>
                  {property.rent_range && (
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700">
                      <Euro className="w-3.5 h-3.5 text-slate-400" />
                      {property.rent_range}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3">
                  <p className={`text-sm leading-snug ${property.prezzo_vendita ? "font-semibold text-emerald-700" : "text-slate-500"}`}>
                    {formatPrezzoVendita(property.prezzo_vendita)}
                  </p>
                  <span className="inline-flex items-center gap-0.5 text-sm font-semibold text-[#4068E8] shrink-0">
                    Apri
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </div>

                {property.note && (
                  <p className="text-sm text-slate-500 mt-3 line-clamp-2 leading-relaxed border-t border-slate-100 pt-3">
                    {property.note}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {properties.length === 0 && (
        <Card className="rounded-3xl border-0 bg-white shadow-sm ring-1 ring-slate-200/60">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-base font-semibold text-slate-800">Nessuna opportunità al momento</p>
            <p className="text-sm text-slate-500 mt-1">Torna più tardi o aggiorna l&apos;elenco</p>
          </CardContent>
        </Card>
      )}

      {properties.length > 0 && filteredProperties.length === 0 && (
        <Card className="rounded-3xl border-0 bg-white shadow-sm ring-1 ring-slate-200/60">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-base font-semibold text-slate-800">Nessun risultato</p>
            <p className="text-sm text-slate-500 mt-1">
              Prova con un&apos;altra città o provincia
            </p>
            <Button variant="outline" className="mt-4 rounded-xl" onClick={() => setSearchQuery("")}>
              Cancella ricerca
            </Button>
          </CardContent>
        </Card>
      )}
    </>
  );

  return (
    <div className={portalMode ? "min-h-[100dvh] bg-[#F4F6FA] flex flex-col" : ""}>
      {portalMode && (
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200/70 px-4 py-3 safe-area-inset-top">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <img src={LOGO_MARK_URL} alt="" className="h-11 w-11 object-contain shrink-0 sm:hidden" />
              <img src={LOGO_URL} alt="Immobiliare by Affidya" className="hidden h-10 w-auto object-contain shrink-0 sm:block" />
              <div className="min-w-0 sm:hidden">
                <h1 className="font-bold text-base text-slate-900 truncate tracking-tight">Portale Immobiliare</h1>
                <p className="text-xs text-slate-500 truncate">{userInfo.name || userInfo.email}</p>
              </div>
              <p className="hidden text-xs text-slate-500 truncate sm:block">{userInfo.name || userInfo.email}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 rounded-2xl bg-white border-slate-200/80 sm:hidden active:scale-95"
                onClick={fetchProperties}
                aria-label="Aggiorna elenco"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              {onLogout && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-11 w-11 rounded-2xl bg-white border-slate-200/80 sm:hidden active:scale-95"
                  onClick={onLogout}
                  aria-label="Esci"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              )}
              <Button variant="outline" size="sm" className="hidden sm:inline-flex rounded-xl h-10" onClick={fetchProperties}>
                <RefreshCw className="w-4 h-4 mr-1.5" />
                Aggiorna
              </Button>
              {onLogout && (
                <Button variant="outline" size="sm" className="hidden sm:inline-flex rounded-xl h-10" onClick={onLogout}>
                  <LogOut className="w-4 h-4 mr-1.5" />
                  Esci
                </Button>
              )}
            </div>
          </div>
        </header>
      )}

      <div className="p-4 sm:p-5 md:p-6 lg:p-8 max-w-6xl mx-auto w-full flex-1 pb-[max(1rem,env(safe-area-inset-bottom))]">
      {showHeader && !portalMode && (
        <PageHeader
          title="Immobiliare"
          subtitle="Opportunità immobiliari per nuove strutture"
          icon={Building2}
          iconContainerClassName="bg-gradient-to-br from-[#4068E8] to-[#3557D4]"
          iconClassName="text-white"
        />
      )}

      {listContent}

      <Dialog open={!!selectedProperty} onOpenChange={(open) => { if (!open) handleCloseDetails(); }}>
        <DialogContent className="flex flex-col max-w-4xl w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-hidden p-0 rounded-none sm:rounded-2xl border-0 left-0 top-0 sm:left-[50%] sm:top-[50%] translate-x-0 translate-y-0 sm:translate-x-[-50%] sm:translate-y-[-50%] gap-0">
          {selectedProperty && (
            <>
              <div className="flex-1 overflow-y-auto overscroll-contain">
                {selectedProperty.photos?.length > 0 && (
                  <div className="relative w-full sm:px-6 sm:pt-6">
                    <div className="w-full h-[min(42vh,320px)] sm:h-[350px] md:h-[400px] sm:rounded-2xl overflow-hidden bg-slate-900 relative flex items-center justify-center">
                      <img
                        src={getPhotoUrl(selectedProperty.photos[selectedPhotoIndex], "large")}
                        alt={`Foto ${selectedPhotoIndex + 1}`}
                        className="max-w-full max-h-full w-auto h-auto object-contain"
                        loading="lazy"
                      />
                      {selectedProperty.photos.length > 1 && (
                        <>
                          <button
                            type="button"
                            onClick={handlePrevPhoto}
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center active:scale-95 z-10"
                            aria-label="Foto precedente"
                          >
                            <ChevronLeft className="w-6 h-6" />
                          </button>
                          <button
                            type="button"
                            onClick={handleNextPhoto}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center active:scale-95 z-10"
                            aria-label="Foto successiva"
                          >
                            <ChevronRight className="w-6 h-6" />
                          </button>
                        </>
                      )}
                      <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm text-white text-sm font-medium px-3 py-1.5 rounded-full z-10">
                        {selectedPhotoIndex + 1} / {selectedProperty.photos.length}
                      </div>
                    </div>
                    {selectedProperty.photos.length > 1 && (
                      <div className="flex gap-2.5 mt-3 px-4 sm:px-0 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-none">
                        {selectedProperty.photos.slice(0, 10).map((photo, index) => (
                          <button
                            key={photo.id || index}
                            type="button"
                            onClick={() => setSelectedPhotoIndex(index)}
                            className={`snap-start flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden ring-2 transition-all ${
                              index === selectedPhotoIndex ? "ring-[#4068E8] scale-[1.02]" : "ring-transparent opacity-80"
                            }`}
                          >
                            <img
                              src={getPhotoUrl(photo, "preview")}
                              alt={`Anteprima ${index + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <DialogHeader className="p-4 sm:p-6 pb-0 pt-5 sm:pt-6 text-left">
                  <div className="flex items-start justify-between gap-3 pr-8">
                    <div className="min-w-0">
                      <DialogTitle className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight tracking-tight">
                        {selectedProperty.name || selectedProperty.address || selectedProperty.location}
                      </DialogTitle>
                      <p className="text-slate-600 mt-2 text-[15px] leading-relaxed">
                        {selectedProperty.location}
                        {selectedProperty.provincia ? ` · ${selectedProperty.provincia}` : ""}
                      </p>
                      {selectedProperty.address && (
                        <p className="text-sm text-slate-400 mt-1">{selectedProperty.address}</p>
                      )}
                    </div>
                    {getStatusBadge(selectedProperty.status)}
                  </div>
                </DialogHeader>

                <div className="space-y-5 sm:space-y-6 p-4 sm:p-6 pt-5 pb-8">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 text-center ring-1 ring-[#4068E8]/10">
                      <Ruler className="w-5 h-5 text-[#4068E8] mx-auto mb-2" />
                      <p className="text-xl font-bold text-slate-900">{selectedProperty.sqm || "N/A"}</p>
                      <p className="text-xs text-slate-500 font-medium mt-0.5">mq</p>
                    </div>
                    <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 text-center ring-1 ring-[#4068E8]/10">
                      <Euro className="w-5 h-5 text-[#4068E8] mx-auto mb-2" />
                      <p className="text-sm font-bold text-slate-900 leading-snug">{selectedProperty.rent_range}</p>
                      <p className="text-xs text-slate-500 font-medium mt-0.5">canone</p>
                    </div>
                    <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 text-center ring-1 ring-[#4068E8]/10">
                      <MapPin className="w-5 h-5 text-[#4068E8] mx-auto mb-2" />
                      <p className="text-sm font-bold text-slate-900 leading-snug">
                        {selectedProperty.provincia || selectedProperty.location}
                      </p>
                      <p className="text-xs text-slate-500 font-medium mt-0.5">provincia</p>
                    </div>
                  </div>

                  {selectedProperty.prezzo_vendita != null && selectedProperty.prezzo_vendita !== 0 && (
                    <div className="rounded-2xl bg-gradient-to-r from-emerald-50 to-emerald-100/40 p-5 ring-1 ring-emerald-500/10">
                      <p className="text-sm text-emerald-700 font-semibold">Prezzo vendita</p>
                      <p className="text-2xl sm:text-3xl font-bold text-emerald-800 tracking-tight mt-1">
                        {formatPrezzoVendita(selectedProperty.prezzo_vendita)}
                      </p>
                    </div>
                  )}

                  {selectedProperty.tipo && (
                    <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200/60">
                      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Tipologia</p>
                      <p className="text-lg font-semibold text-slate-900 mt-1">{selectedProperty.tipo}</p>
                    </div>
                  )}

                  {selectedProperty.posti_letto && (
                    <div className="rounded-2xl bg-emerald-50 p-4 ring-1 ring-emerald-500/10">
                      <p className="text-sm text-emerald-700 font-semibold">Posti letto autorizzati</p>
                      <p className="text-2xl font-bold text-emerald-800 mt-1">{selectedProperty.posti_letto}</p>
                    </div>
                  )}

                  <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-200/60">
                    <h4 className="font-bold text-slate-900 mb-2">Descrizione</h4>
                    <p className="text-[15px] text-slate-600 leading-relaxed whitespace-pre-wrap">
                      {selectedProperty.note || "Nessuna descrizione disponibile"}
                    </p>
                  </div>

                  {selectedProperty.planimetria_url && (
                    <div className="rounded-2xl bg-red-50/80 p-4 ring-1 ring-red-200/60">
                      <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-red-500" />
                        Planimetria
                      </h4>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                            <FileText className="w-6 h-6 text-red-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800 truncate">
                              {selectedProperty.planimetria_name || "Planimetria.pdf"}
                            </p>
                            <p className="text-sm text-slate-500">Documento PDF</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          className="w-full sm:w-auto h-12 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                          onClick={downloadPlanimetria}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Scarica
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="shrink-0 border-t border-slate-200/80 bg-white/95 backdrop-blur-sm p-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col sm:flex-row gap-3 shadow-[0_-8px_30px_rgba(15,23,42,0.08)]">
                <Button variant="outline" className="h-[52px] rounded-2xl sm:flex-1 text-base" onClick={handleCloseDetails}>
                  Chiudi
                </Button>
                <Button
                  className="h-[52px] rounded-2xl sm:flex-1 text-base bg-gradient-to-r from-[#4068E8] to-[#3557D4] hover:from-[#3557D4] hover:to-[#2F4CC4] shadow-md shadow-blue-500/20"
                  onClick={() => setShowContactModal(true)}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Richiedi informazioni
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-[480px] fixed bottom-0 left-0 right-0 top-auto translate-x-0 translate-y-0 sm:bottom-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] rounded-t-[28px] sm:rounded-2xl border-0 p-0 gap-0 max-h-[92dvh] overflow-hidden flex flex-col">
          <div className="mx-auto w-10 h-1 rounded-full bg-slate-200 mt-3 mb-1 sm:hidden shrink-0" aria-hidden />
          <DialogHeader className="px-6 pt-4 pb-2 text-left shrink-0">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <MessageSquare className="w-5 h-5 text-[#4068E8]" />
              Richiedi informazioni
            </DialogTitle>
            <DialogDescription className="text-[15px] text-slate-500 leading-relaxed">
              {selectedProperty?.name || selectedProperty?.address || "Immobile selezionato"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-6 py-2 overflow-y-auto flex-1">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">Motivo di contatto</Label>
              <Select
                value={contactForm.motivo}
                onValueChange={(value) => setContactForm((prev) => ({ ...prev, motivo: value }))}
              >
                <SelectTrigger className="h-[52px] rounded-2xl text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="informazioni">Richiedere informazioni</SelectItem>
                  <SelectItem value="appuntamento">Prendere un appuntamento</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <User className="w-4 h-4 text-slate-400" />
                Nome
              </Label>
              <Input
                value={contactForm.nome}
                onChange={(e) => setContactForm((prev) => ({ ...prev, nome: e.target.value }))}
                placeholder="Il tuo nome"
                className="h-[52px] rounded-2xl text-base"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Phone className="w-4 h-4 text-slate-400" />
                Telefono
              </Label>
              <Input
                value={contactForm.telefono}
                onChange={(e) => setContactForm((prev) => ({ ...prev, telefono: e.target.value }))}
                placeholder="Il tuo numero di telefono"
                inputMode="tel"
                className="h-[52px] rounded-2xl text-base"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Mail className="w-4 h-4 text-slate-400" />
                Email
              </Label>
              <Input
                value={contactForm.email}
                onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="La tua email"
                type="email"
                inputMode="email"
                className="h-[52px] rounded-2xl text-base"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <MessageSquare className="w-4 h-4 text-slate-400" />
                Richieste <span className="text-slate-400 text-xs font-normal">(facoltativo)</span>
              </Label>
              <Textarea
                value={contactForm.richieste}
                onChange={(e) => setContactForm((prev) => ({ ...prev, richieste: e.target.value }))}
                placeholder="Scrivi qui eventuali richieste o domande..."
                rows={4}
                className="rounded-2xl text-base min-h-[100px] resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-3 px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-slate-100 shrink-0 flex-col sm:flex-row">
            <Button variant="outline" className="h-[52px] rounded-2xl w-full sm:w-auto text-base" onClick={() => setShowContactModal(false)}>
              Annulla
            </Button>
            <Button
              className="h-[52px] rounded-2xl w-full sm:flex-1 text-base bg-gradient-to-r from-[#4068E8] to-[#3557D4] hover:from-[#3557D4] hover:to-[#2F4CC4]"
              onClick={handleSendRequest}
              disabled={sendingRequest}
            >
              {sendingRequest ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Invio...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Invia richiesta
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
